import React from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Bell, Search, SlidersHorizontal, MapPin, Clock, Users, Calendar, Sparkles, ArrowRight, Heart } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1722410141874-5494d14deeca?auto=format&fit=crop&w=1400&q=80";
const COFFEE_IMG = "https://images.pexels.com/photos/36765297/pexels-photo-36765297.jpeg?auto=compress&cs=tinysrgb&w=900";
const SPORTS_IMG = "https://images.unsplash.com/photo-1772517403292-b68a77fbc342?auto=format&fit=crop&w=900&q=80";
const DINING_IMG = "https://images.pexels.com/photos/25409638/pexels-photo-25409638.jpeg?auto=compress&cs=tinysrgb&w=900";

const categories = [
  { name: "Sports", icon: "⚽" },
  { name: "Indoor Sports", icon: "🏓" },
  { name: "Board Games", icon: "🎲" },
  { name: "Dining", icon: "🍽️" },
  { name: "Bars", icon: "🍻" },
  { name: "Clubs", icon: "💃" },
  { name: "Beach", icon: "🏖️" },
  { name: "Concerts", icon: "🎤" },
  { name: "Art & Crafts", icon: "🎨" },
];

export default function Landing() {
  const { t } = useLang();
  const nav = useNavigate();

  return (
    <div className="min-h-screen relative" data-testid="landing-page">
      {/* Forest-green header band */}
      <div className="header-band relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{
          backgroundImage: "radial-gradient(circle at 20% 20%, #fff 1px, transparent 1px), radial-gradient(circle at 80% 60%, #fff 1px, transparent 1px)",
          backgroundSize: "32px 32px, 48px 48px",
        }} />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 pb-12 relative z-10">
          <div className="flex items-center justify-between text-white/90 text-sm">
            <div className="flex items-center gap-1.5">
              <MapPin className="h-4 w-4" />
              <span>Find your local meetups</span>
            </div>
            <div className="flex items-center gap-3">
              <Bell className="h-4 w-4" />
              <Button size="sm" variant="ghost" onClick={() => nav("/login")} data-testid="hero-top-login" className="text-white hover:text-white hover:bg-white/10 rounded-full h-8">
                {t("login")}
              </Button>
            </div>
          </div>

          <div className="mt-10 grid grid-cols-12 gap-6">
            <div className="col-span-12 lg:col-span-7 fade-up">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 border border-white/20 text-xs uppercase tracking-[0.18em] text-white/95 font-semibold backdrop-blur">
                <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--primary))] pulse-dot" />
                real meetups · within 5 km
              </div>
              <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-heading font-semibold leading-[1.05] tracking-tight text-white">
                {t("tagline")}
              </h1>
              <p className="mt-5 text-base sm:text-lg text-white/80 max-w-xl leading-relaxed">
                {t("heroSub")}
              </p>

              {/* Search bar — decorative on landing */}
              <div className="mt-7 max-w-md flex items-center gap-2 bg-white rounded-2xl p-2 shadow-[0_8px_28px_rgba(0,0,0,0.18)]">
                <div className="flex-1 flex items-center gap-2 px-3">
                  <Search className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
                  <span className="text-sm text-[hsl(var(--muted-foreground))]">Search activities…</span>
                </div>
                <Button
                  onClick={() => nav("/register")}
                  data-testid="hero-cta-register"
                  className="rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-hover))] text-white px-5 h-10 font-medium"
                >
                  {t("getStarted")}
                </Button>
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <Button
                  size="lg"
                  variant="ghost"
                  onClick={() => nav("/login")}
                  data-testid="hero-cta-login"
                  className="rounded-full px-6 py-5 text-base text-white hover:bg-white/10 hover:text-white"
                >
                  {t("login")} <ArrowRight className="ms-2 h-4 w-4 rtl:rotate-180" />
                </Button>
              </div>
            </div>

            {/* Right phone-mockup card */}
            <div className="col-span-12 lg:col-span-5 relative">
              <div className="relative rounded-[2rem] overflow-hidden border border-white/20 shadow-[0_24px_60px_rgba(0,0,0,0.35)] aspect-[4/5] backdrop-blur">
                <img src={HERO_IMG} alt="People hiking" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute top-4 end-4 h-10 w-10 rounded-full bg-white/95 flex items-center justify-center shadow-lg">
                  <Heart className="h-5 w-5 text-[hsl(var(--primary))]" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <div className="bg-white rounded-2xl p-4 border border-white/40 shadow-xl">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-heading font-semibold text-[hsl(var(--foreground))]">Weekend Hike</div>
                        <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5 flex items-center gap-1">
                          <MapPin className="h-3 w-3" /> 2.1 km · Outdoor
                        </div>
                      </div>
                      <Button size="sm" className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-hover))] text-white h-8 px-3 text-xs">
                        + Attend
                      </Button>
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex -space-x-2">
                        {["A","B","C"].map((c, i) => (
                          <div key={i} className="h-6 w-6 rounded-full bg-[hsl(var(--secondary))] text-white text-[10px] flex items-center justify-center font-semibold border-2 border-white">{c}</div>
                        ))}
                      </div>
                      <span className="text-xs text-[hsl(var(--muted-foreground))]">+12 attending</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="absolute -bottom-6 -start-6 hidden sm:block rotate-[-4deg]">
                <div className="rounded-2xl overflow-hidden border-2 border-white shadow-xl w-40 h-28">
                  <img src={COFFEE_IMG} alt="" className="w-full h-full object-cover" />
                </div>
              </div>
              <div className="absolute -top-4 -end-4 hidden sm:block rotate-[5deg]">
                <div className="rounded-2xl overflow-hidden border-2 border-white shadow-xl w-32 h-24">
                  <img src={SPORTS_IMG} alt="" className="w-full h-full object-cover" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* White content area */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 -mt-6 relative z-10 pb-24">
        {/* Categories */}
        <div className="bg-white border border-[hsl(var(--border))] rounded-3xl p-6 sm:p-8 shadow-[0_8px_40px_rgba(31,30,27,0.06)]">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-xl font-semibold">Categories</h2>
            <button onClick={() => nav("/register")} data-testid="see-all-categories" className="text-sm font-medium text-[hsl(var(--primary))] hover:underline">See all</button>
          </div>
          <div className="mt-5 grid grid-cols-3 sm:grid-cols-5 md:grid-cols-9 gap-3 sm:gap-4">
            {categories.map((c) => (
              <button key={c.name} onClick={() => nav("/register")} className="group flex flex-col items-center gap-2" data-testid={`cat-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-2xl bg-[hsl(var(--elevated))] border border-[hsl(var(--border))] flex items-center justify-center text-2xl sm:text-3xl group-hover:-translate-y-0.5 group-hover:border-[hsl(var(--primary))] transition-all">
                  {c.icon}
                </div>
                <span className="text-[11px] sm:text-xs text-[hsl(var(--foreground))] font-medium text-center leading-tight">{c.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Top picks — event-style cards */}
        <div className="mt-8">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-2xl font-semibold">Top Picks Near You</h2>
            <button onClick={() => nav("/register")} className="text-sm font-medium text-[hsl(var(--primary))] hover:underline">See all</button>
          </div>

          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
            {[
              { img: SPORTS_IMG, title: "Saturday Pickup Football", cat: "Sports", time: "10:00 am – 12:00 pm", attendees: 23, distance: "1.4 km" },
              { img: DINING_IMG, title: "Local Foodie Tour", cat: "Dining", time: "7:30 pm – 11:00 pm", attendees: 41, distance: "2.0 km" },
              { img: HERO_IMG, title: "Sunset Hike at the Ridge", cat: "Outdoor", time: "5:30 pm – 8:00 pm", attendees: 28, distance: "3.2 km" },
              { img: COFFEE_IMG, title: "Coffee & Conversation", cat: "Dining", time: "9:00 am – 11:00 am", attendees: 12, distance: "0.8 km" },
            ].map((e, i) => (
              <div key={i} className="bg-white rounded-3xl border border-[hsl(var(--border))] overflow-hidden hover:-translate-y-1 transition-all shadow-[0_4px_24px_rgba(31,30,27,0.05)]">
                <div className="relative h-44 overflow-hidden">
                  <img src={e.img} alt="" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute top-3 end-3 h-9 w-9 rounded-full bg-white/95 flex items-center justify-center">
                    <Heart className="h-4 w-4 text-[hsl(var(--foreground))]" />
                  </div>
                  <span className="absolute top-3 start-3 text-[10px] uppercase tracking-wider font-bold bg-white px-2.5 py-1 rounded-full text-[hsl(var(--secondary))] border border-white/40">
                    {e.cat}
                  </span>
                </div>
                <div className="p-5">
                  <h3 className="font-heading text-lg font-semibold">{e.title}</h3>
                  <div className="mt-2 flex items-center gap-1 text-sm text-[hsl(var(--muted-foreground))]">
                    <Clock className="h-3.5 w-3.5" /> {e.time}
                  </div>
                  <div className="mt-4 flex items-center justify-between">
                    <div className="flex items-center gap-3 text-xs text-[hsl(var(--muted-foreground))]">
                      <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5 text-[hsl(var(--primary))]" /> +{e.attendees}</span>
                      <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-[hsl(var(--primary))]" /> {e.distance}</span>
                    </div>
                    <Button onClick={() => nav("/register")} size="sm" className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-hover))] text-white h-9 px-4">
                      RSVP
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Feature row */}
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { icon: Sparkles, title: "AI matchmaking", desc: "Smart suggestions based on activities, languages and your city." },
            { icon: Calendar, title: "WhatsApp-style chat", desc: "Real conversations 1-on-1 or with up to 10 in a group." },
            { icon: Users, title: "Local groups", desc: "Create groups and send invites — accept or decline freely." },
          ].map((f, i) => (
            <div key={i} className="bg-white border border-[hsl(var(--border))] rounded-2xl p-6 hover:-translate-y-1 transition-all">
              <div className="h-11 w-11 rounded-2xl bg-[hsl(var(--elevated))] flex items-center justify-center text-[hsl(var(--primary))]">
                <f.icon className="h-5 w-5" />
              </div>
              <div className="mt-4 font-heading text-lg font-medium">{f.title}</div>
              <div className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">{f.desc}</div>
            </div>
          ))}
        </div>

        <div className="mt-14 text-center">
          <Button
            size="lg"
            onClick={() => nav("/register")}
            data-testid="footer-cta-register"
            className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-hover))] text-white px-8 py-6 text-base font-medium hover:-translate-y-0.5 transition-all shadow-[0_8px_24px_rgba(22,200,90,0.3)]"
          >
            {t("getStarted")} <ArrowRight className="ms-2 h-4 w-4 rtl:rotate-180" />
          </Button>
        </div>

        <footer className="mt-16 pt-8 border-t border-[hsl(var(--border))] text-center text-xs text-[hsl(var(--muted-foreground))]">
          <a href="/privacy" data-testid="footer-privacy-link" className="hover:text-[hsl(var(--primary))]">
            Privacy Policy
          </a>
          <span className="mx-2">·</span>
          <span>© {new Date().getFullYear()} SocialHub</span>
        </footer>
      </div>
    </div>
  );
}
